// apps/web/src/routes/$lang/_layout/place.$city.tsx
import { createFileRoute, redirect } from "@tanstack/react-router";
import { parseLang } from "@majourneys/shared";
import { ExplorePage } from "../../../features/activities/pages/explore-page";
import {
  activitiesQueryOptions,
  buildActivityFilters,
  cityExploreLinksQueryOptions,
  type ActivityRouteSearch,
} from "../../../features/activities/api/activities-query";
import {
  categoryBySlugQueryOptions,
  cityBySlugQueryOptions,
} from "../../../features/activities/hooks/use-seo-lookup";
import { cityDisplayName } from "../../../features/activities/lib/city-display-name";
import { cityArticlesQueryOptions } from "../../../features/blog/hooks/use-city-articles";
import { prefetchEmptyHubRail } from "../../../features/activities/api/empty-hub-prefetch";
import { isEmptyCityHubView, listTotal } from "../../../features/activities/lib/nearby-bookable-cities";
import { ensureHubSlugsExist } from "../../../features/activities/api/hub-guard";
import { categoriesQueryOptions } from "../../../features/activities/hooks/use-categories";
import { citiesQueryOptions } from "../../../features/activities/hooks/use-cities";
import { canonicalCitySlug } from "../../../features/activities/lib/city-aliases";

export const Route = createFileRoute("/$lang/_layout/place/$city")({
  // Another spelling of a city we have (/es/place/fes, /ar/place/tanger)
  // 301s to the real slug with the query kept, instead of a 404.
  beforeLoad: ({ params }) => {
    const city = canonicalCitySlug(params.city);
    if (city) {
      throw redirect({
        to: "/$lang/place/$city",
        params: { lang: params.lang, city },
        search: (prev) => prev,
        statusCode: 301,
      });
    }
  },
  // Prefetch the grid + SEO-override data in the loader so the router keeps the
  // previous page on screen during navigation instead of dropping into the
  // full-page Suspense spinner. `defaultPreload: "intent"` also warms this on
  // hover, so most city/category switches resolve instantly.
  loaderDeps: ({ search }: { search: ActivityRouteSearch }) => ({
    q: search.q,
    page: search.page,
    min_price: search.min_price,
    max_price: search.max_price,
    sort: search.sort,
    show: search.show,
  }),
  loader: async ({ context, params, deps }) => {
    const filters = buildActivityFilters(params, deps);
    // `CityExploreLinks` renders on the city overview only — the same
    // condition `ExplorePage` uses — so its data is prefetched under exactly
    // the same condition. Prefetching here rather than letting the component
    // fetch on mount is the whole point: it is what puts those anchors in the
    // server-rendered HTML where a crawler can follow them.
    const isCityOverview = !deps.q?.trim();
    // An unknown city is a 404, not an empty page (see hub-guard.ts).
    await ensureHubSlugsExist(context.queryClient, { city: params.city });
    const [listResponse, cityRow] = await Promise.all([
      context.queryClient.ensureQueryData(activitiesQueryOptions(filters)),
      context.queryClient.ensureQueryData(cityBySlugQueryOptions(params.city)),
      context.queryClient.ensureQueryData(categoryBySlugQueryOptions(undefined)),
      // The cross-links read these to link only hubs with something in them.
      context.queryClient.ensureQueryData(categoriesQueryOptions(params.city, params.lang)),
      context.queryClient.ensureQueryData(citiesQueryOptions(params.lang)),
      ...(isCityOverview
        ? [
            context.queryClient.ensureQueryData(
              cityExploreLinksQueryOptions(params.city, params.lang),
            ),
          ]
        : []),
    ]);
    // Nothing to book here: warm the "book nearby" rail so its cards reach
    // the server HTML. Best-effort, and it never throws.
    if (
      isEmptyCityHubView({
        city: params.city,
        total: listTotal(listResponse),
        q: deps.q,
        minPrice: deps.min_price,
        maxPrice: deps.max_price,
      })
    ) {
      await prefetchEmptyHubRail(context.queryClient, {
        citySlug: params.city,
        lang: params.lang ?? "en",
        includeSelf: false,
      });
    }
    // Prefetch the "Read about {city}" articles under the same key
    // `useCityArticles` reads, so the article anchors are in the SSR HTML.
    // Without this the section only mounts after hydration and crawlers see a
    // city hub that never links to its own editorial coverage — which is how
    // /place/tetouan ended up ranking (badly) for queries the Tetouan article
    // should own. Best-effort: an articles hiccup must not 500 the hub.
    const lang = parseLang(params.lang);
    await context.queryClient
      .ensureQueryData(
        cityArticlesQueryOptions(
          lang,
          cityDisplayName(cityRow, lang, params.city),
        ),
      )
      .catch(() => undefined);
  },
  component: ExplorePage,
});
